import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { Box, Button, Checkbox, Collapse, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { getEmailInboxInfo } from '@/utils/email-inbox-url';
import AuthSplitLayout from './AuthSplitLayout';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';
import { betterAuthClient } from '@/libs/better-auth-client';
import { getRedirectPath, redirectAfterAuth } from './auth-utils';

type AuthTwoFactorLocationState = {
    redirectPath?: string;
    oauthQuery?: string;
    methods?: string[];
    email?: string;
} | null;

const RESEND_COOLDOWN_SECONDS = 30;

export default function AuthTwoFactor() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();

    const locationState = location.state as AuthTwoFactorLocationState;
    const redirectPath = useMemo(
        () => locationState?.redirectPath || getRedirectPath(),
        [locationState?.redirectPath],
    );
    const oauthQuery = locationState?.oauthQuery;

    // Email OTP when the backend offers 'otp' and no authenticator ('totp'); otherwise keep the
    // TOTP flow. The mandatory-OTP bridge returns methods: ['otp'] for users without an authenticator.
    const methods = locationState?.methods;
    const mode: 'otp' | 'totp' =
        methods?.length && methods.includes('otp') && !methods.includes('totp') ? 'otp' : 'totp';

    // Inbox shortcut ("Open Gmail", …) for the OTP email, for known webmail providers.
    const email = locationState?.email;
    const inboxInfo = useMemo(() => (email ? getEmailInboxInfo(email) : null), [email]);

    type BetterAuthResult<TData> = {
        data?: TData | null;
        error?: {
            message?: string;
        } | null;
    };

    const continueOAuthIfNeeded = async (): Promise<boolean> => {
        if (!oauthQuery) {
            return false;
        }

        const result: BetterAuthResult<{ url?: string }> = await betterAuthClient.oauth2.continue({
            postLogin: true,
            oauth_query: oauthQuery,
        });

        if (result.error) {
            enqueueSnackbar(
                typeof result.error.message === 'string' ? result.error.message : t('auth.oauth.error.default'),
                { variant: 'error' },
            );
            return false;
        }

        const redirectUrl = result.data?.url;
        if (redirectUrl) {
            window.location.replace(redirectUrl);
            return true;
        }

        return false;
    };

    const [code, setCode] = useState('');
    const [trustDevice, setTrustDevice] = useState(false);
    const [loading, setLoading] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const sendOtp = async (isResend: boolean) => {
        setSending(true);
        try {
            await authApi.sendOtp();
            setSent(true);
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            if (isResend) {
                enqueueSnackbar(t('auth.two-factor.otp.resent'), { variant: 'success' });
            }
        } catch (error) {
            enqueueSnackbar(mapAuthError(error, t, 'two-factor').message, { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    // Auto-send the email OTP once on mount for the OTP flow. The ref guard survives React
    // StrictMode's double-invoke (same fiber) so only ONE code is sent.
    const autoSentRef = useRef(false);
    useEffect(() => {
        if (mode !== 'otp' || autoSentRef.current) {
            return;
        }
        autoSentRef.current = true;
        void sendOtp(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // Resend cooldown ticker.
    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timer = setInterval(() => setResendCooldown((prev) => Math.max(0, prev - 1)), 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const normalizedCode = code.replace(/\D/g, '').slice(0, 6);
        if (normalizedCode.length !== 6) {
            setCodeError(t('auth.two-factor.error.invalid'));
            return;
        }

        setCodeError('');
        setLoading(true);
        try {
            if (mode === 'otp') {
                await authApi.verifyOtp({ code: normalizedCode, trustDevice });
            } else {
                await authApi.verifyTotp({ code: normalizedCode, trustDevice });
            }
            const continued = await continueOAuthIfNeeded();
            if (!continued) {
                redirectAfterAuth(redirectPath);
            }
        } catch (error) {
            const mapped = mapAuthError(error, t, 'two-factor');
            if (mapped.field === 'code') {
                setCodeError(mapped.message);
            } else {
                enqueueSnackbar(mapped.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthSplitLayout>
            <Stack spacing={3}>
                <Box>
                    <Typography
                        component={Link}
                        to="/auth/login"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 16 }} />
                        {t('auth.two-factor.back-to-login')}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.two-factor.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {mode === 'otp' ? t('auth.two-factor.otp.subtitle') : t('auth.two-factor.subtitle')}
                    </Typography>
                </Box>

                <Stack component="form" spacing={2} onSubmit={onSubmit}>
                    <TextField
                        label={t('auth.two-factor.code-label')}
                        value={code}
                        onChange={(event) => {
                            setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                            if (codeError) setCodeError('');
                        }}
                        required
                        autoFocus
                        autoComplete="one-time-code"
                        fullWidth
                        error={!!codeError}
                        helperText={codeError || undefined}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            maxLength: 6,
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={trustDevice}
                                onChange={(_, checked) => setTrustDevice(checked)}
                            />
                        }
                        label={t('auth.two-factor.trust-device')}
                    />

                    <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
                        {loading ? t('auth.common.loading') : t('auth.two-factor.submit')}
                    </Button>

                    {mode === 'otp' && (
                        <>
                            <Box textAlign="center">
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    disabled={sending || resendCooldown > 0}
                                    onClick={() => sendOtp(true)}
                                >
                                    {resendCooldown > 0
                                        ? t('auth.two-factor.otp.resend-in', { seconds: resendCooldown })
                                        : t('auth.two-factor.otp.resend')}
                                </Button>
                            </Box>

                            {/* Inbox shortcut — below the resend, mirroring the magic-link flow */}
                            {inboxInfo && (
                                <Collapse in={sent} timeout={280}>
                                    <Button
                                        component="a"
                                        href={inboxInfo.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="outlined"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        endIcon={<OpenInNewRounded sx={{ fontSize: '0.85rem !important' }} />}
                                    >
                                        {t('auth.magic-link.open-inbox', { provider: t(inboxInfo.i18nKey) })}
                                    </Button>
                                </Collapse>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
        </AuthSplitLayout>
    );
}
